<script lang="ts">
	import { signOut } from '$lib/authentication/firebase'
	import authState from '$lib/stores/auth-state'
	import modal from '$lib/stores/modal-state'
	import Button from '../buttons/Button.svelte'
	import MenuIcon from '../icons/MenuIcon.svelte'
	import { track } from '$lib/utils/track'
	import MobileNavigation from './MobileNavigation.svelte'
	import { goto } from '$app/navigation'
	import { getContextClient } from '@urql/svelte'
	import { clearInsertTripOnLoadParams, loginAndInsertTrip } from '$lib/utils/insertTrip'
	import { performSignIn } from '$lib/authentication/helpers'
	import {
		InsertTripDocument,
		type InsertTripMutation,
		type InsertTripMutationVariables,
	} from '$lib/queries/generated/InsertTrip'
	import Avatar from '../profiles/Avatar.svelte'
	import { stopPropagation } from '$lib/utils/clickHelpers'

	let user = $derived($authState.currentUser)
	let avatarSrc = $derived(
		user?.currentProfilePictureUrl && user?.currentProfilePictureUrl != ''
			? user?.currentProfilePictureUrl
			: '/visuals/icons/default-user.svg',
	)

	let signedIn = $derived($authState.signedIn)

	let navType: NavType = $state(null)

	const client = getContextClient()
	const insertTripMutation = (vars: InsertTripMutationVariables) =>
		client
			.mutation<InsertTripMutation, InsertTripMutationVariables>(InsertTripDocument, vars)
			.toPromise()

	function handleStartTripClick() {
		track('Start Trip Clicked')
		if ($authState.signedIn) {
			handleInsertTrip()
		} else {
			loginAndInsertTrip(
				performSignIn,
				() => handleInsertTrip().then(clearInsertTripOnLoadParams),
				null,
			)

			return Promise.resolve()
		}
	}

	async function handleInsertTrip() {
		insertTripMutation({ input: { title: 'Add a Name' } })
			.then((res) => {
				if (res?.data?.insertTrip?.id) {
					goto(`/trips/${res?.data?.insertTrip?.id}`)
				}
			})
			.catch((err) => {
				console.error(err)
			})
	}

	function handleSignIn() {
		track('Sign In Clicked')
		modal.open('signup', { intent: 'signIn' })
	}

	function handleClickOnMobile() {
		track('Mobile Menu Clicked')
		navType = 'mobile'
	}

	function handleSignOut() {
		track('Sign Out Clicked')
		signOut()
	}

	function setClose() {
		navType = null
	}

	function handleNavigate(target: string) {
		track('Navigation Clicked', { target })
		goto(target).then(() => {
			setClose()
		})
	}

	const openMailer = () => {
		track('Open Mailer', { category: 'Footer' })
		window.open('https://intercom.help/airheart/en', '_blank')
	}

	let name = $derived($authState.currentUser?.currentName || '')
</script>

<button
	aria-label="Open Menu"
	type="button"
	class={`z-40 fixed mx-auto top-2 mb-[2px] right-2 flex items-center text-base font-medium whitespace-nowrap focus:outline-none focus:ring-transparent focus:ring-offset-0 dark:text-brand-gray-0 hover:text-white p-1 bg-white dark:bg-brand-gray-5 rounded-full touch-manipulation ${
		signedIn ? 'gap-x-1 pl-2' : 'w-10 h-10'
	}`}
	onclick={stopPropagation(handleClickOnMobile)}
>
	<MenuIcon class={`h-5 w-5 m-auto`} />
	{#if signedIn}
		<Avatar size="sm" src={avatarSrc} alt={name} />
	{/if}
</button>

<MobileNavigation isOpen={navType === 'mobile'} onclose={setClose}>
	<nav class="grid gap-y-4">
		<button
			onclick={stopPropagation(() => handleNavigate('/explore'))}
			class="px-4 text-base font-medium tracking-tight text-start touch-manipulation"
			>Explore Guides</button
		>
		{#if signedIn}
			<button
				onclick={stopPropagation(() => handleNavigate('/trips'))}
				class="px-4 text-base font-medium tracking-tight text-start touch-manipulation"
				>Your Trips</button
			>
			<button
				onclick={stopPropagation(() => handleNavigate('/store'))}
				class="px-4 text-base font-medium tracking-tight text-start touch-manipulation"
				>Your Store</button
			>
		{/if}
		<div class="h-[1px] bg-brand-gray-4/25 mt-2">
			<!-- save space  -->
		</div>

		<button
			onclick={stopPropagation(openMailer)}
			class="px-4 text-base font-medium tracking-tight text-start touch-manipulation"
			>Get Help</button
		>
		<button
			onclick={stopPropagation(() => handleNavigate('/guide'))}
			class="px-4 text-base font-medium tracking-tight text-start touch-manipulation"
			>Sell Your Guides</button
		>
		<button
			onclick={stopPropagation(() => handleNavigate('/about'))}
			class="px-4 text-base font-medium tracking-tight text-start touch-manipulation"
			>About Us</button
		>

		<Button onclick={handleStartTripClick} theme="brand" class="mt-2" size="sm">Start a Trip</Button
		>
	</nav>
	<div class="px-5 mt-6 pt-6 space-y-6">
		{#if signedIn}
			<div class="px-4 py-4 text-center select-none">
				<Avatar size="lg" src={avatarSrc} alt={user?.currentName} />
				<div class="mt-2 leading-4">{user?.currentName}</div>
				<div class="m-0 mt-1 text-xs leading-4 text-brand-secondary">{user?.email}</div>
			</div>
			<div class="text-center">
				<button
					aria-label="Sign out"
					class="text-black dark:text-white focus:outline-none hover:text-opacity-80 touch-manipulation"
					onclick={stopPropagation(handleSignOut)}
				>
					Sign Out
				</button>
			</div>
		{:else}
			<div class="flex flex-col space-y-4 w-full justify-center items-center">
				<p class="text-base font-medium text-center text-gray-900">
					<button
						aria-label="Sign in"
						onclick={stopPropagation(handleSignIn)}
						class="text-black dark:text-white focus:outline-none hover:text-opacity-80 touch-manipulation"
					>
						Sign In
					</button>
				</p>
			</div>
		{/if}
	</div>
</MobileNavigation>
